/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2021-2025 (original work) Open Assessment Technologies SA
 */
import { add as addToAcsStore, resolve as resolveAcs } from '@/store/acs';
import request, { getEndpointUrl } from '@/core/api/apiRequest';
import * as Types from '../../core/model/types.js'; // eslint-disable-line no-unused-vars
import { actionsACSActionsMap } from '@/core/utils/consts';

/**
 * Requests LTI-BE to perform an ACS
 * @param {Types.ACSInteraction[]} data - required by LTI-BE to perform ACS
 * @returns {Promise} - Request promise
 */

export function postAcs(data) {
    addToAcsStore(data);

    const acsPayload = data.reduce((acc, item, i) => {
        acc[i] = { ...item, action: actionsACSActionsMap[item.action] || item.action };
        return acc;
    }, {});

    const body = buildFormDataObject(acsPayload);
    const url = getEndpointUrl({ domain: 'LTI_BACKEND_SERVICE', endpoint: 'acs' }, {}, {});

    const options = {
        method: 'POST',
        body
    };

    return request(url, options).then(res => {
        resolveAcs(res);
        // setTimeout(() => resolveAcs(res), 5000); // useful for local testing of loading spinner during ACS resolution
    });
}

/**
 * Builds a FormData instance with required data for each ACS to request
 * @param {Object} data - Object containing required data info for each ACS to request
 * @returns {FormData}
 */
function buildFormDataObject(data) {
    const res = new FormData();

    for (let key in data) {
        for (let innerKey in data[key]) {
            res.append(`${key}[${innerKey}]`, data[key][innerKey]);
        }
    }

    return res;
}

/**
 * Requests LTI-BE to authorize the given actions.
 * 'authorize' is not a true ACS, that's why the endpoint is different;
 * but within the frontend we can treat it like an ACS, so the data flows
 * through the same stores.
 * @param {Types.ACSInteraction[]} actions
 * @returns {Promise} - Request promise
 */
export async function postAuthorize(actions = []) {
    addToAcsStore(actions);

    const assessmentIds = actions.map(action => action.delivery_execution_id).filter(Boolean);

    const body = JSON.stringify({
        assessmentIds
    });
    const url = getEndpointUrl({ domain: 'LTI_BACKEND_SERVICE', endpoint: 'authorize' }, {}, {});

    const options = {
        method: 'POST',
        body
    };

    return request(url, options).then(({ success, notFound }) => {
        if (success) {
            for (const action of actions) {
                action.success = !notFound.includes(action.delivery_execution_id);
            }
        } else {
            for (const action of actions) {
                action.success = false;
            }
        }

        const objectActions = {};
        for (const action of actions) {
            objectActions[action.delivery_execution_id] = action;
        }
        resolveAcs(objectActions);
    });
}

/**
 * Requests LTI-BE to delete (reset) delivery executions
 * @param {Types.ACSInteraction[]} actions
 * @returns {Promise} - Request promise
 */

export async function deleteDeliveryExecution(actions) {
    addToAcsStore(actions);

    const requestPromises = actions.map((action) => {
        const assessmentId = action.delivery_execution_id;

        const url = getEndpointUrl(
            {
                domain: 'LTI_BACKEND_SERVICE',
                endpoint: 'deliveryExecution'
            },
            { id: encodeURIComponent(assessmentId) },
            {}
        );
        const options = {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                extra: {
                    acsLog: action
                }
            })
        };

        return request(url, options);
    });

    const results = await Promise.allSettled(requestPromises);

    results.forEach((result, index) => {
        actions[index].success = result.status === 'fulfilled';
    });

    const objectActions = {};
    for (const action of actions) {
        objectActions[action.delivery_execution_id] = action;
    }
    resolveAcs(objectActions);
}
